import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Flex, Tabs } from "@mantine/core";
import { SvgIcon } from "@mui/material";
import React, { useEffect, useState } from "react";
const cleanStringForUrl = (str) => {
    return str
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "") // Remove all non-alphanumeric characters
        .trim();
};
export const TabGroup = ({ children, changeUrl = true }) => {
    // Get initial tab from URL hash or default to "0"
    const [activeTab, setActiveTab] = useState(() => {
        // If changeUrl is false, always start with the first tab
        if (!changeUrl)
            return "0";
        const hash = window.location.hash.replace("#", "");
        // Try to find tab index by label, fallback to hash or "0"
        const childArray = Array.isArray(children) ? children : [children];
        const tabIndex = childArray.findIndex((child) => React.isValidElement(child) &&
            cleanStringForUrl(child.props
                .label || "") === cleanStringForUrl(hash));
        return tabIndex >= 0 ? tabIndex.toString() : hash || "0";
    });
    // Update URL hash when tab changes.
    const handleTabChange = (value) => {
        if (!value)
            return;
        const childArray = Array.isArray(children) ? children : [children];
        const child = childArray[parseInt(value)];
        // Only update URL if changeUrl is true
        if (changeUrl && React.isValidElement(child)) {
            const label = child.props
                .label;
            window.location.hash = label ? cleanStringForUrl(label) : value;
        }
        setActiveTab(value);
    };
    // Listen for browser back/forward buttons
    useEffect(() => {
        const handleHashChange = () => {
            // Only handle hash changes if changeUrl is true
            if (!changeUrl)
                return;
            // Get hash without query parameters
            const hash = window.location.hash.split("?")[0].replace("#", "");
            const childArray = Array.isArray(children) ? children : [children];
            const tabIndex = childArray.findIndex((child) => React.isValidElement(child) &&
                cleanStringForUrl(child.props
                    .label || "") === cleanStringForUrl(hash));
            setActiveTab(tabIndex >= 0 ? tabIndex.toString() : "0");
        };
        // Initial check when component mounts
        handleHashChange();
        window.addEventListener("hashchange", handleHashChange);
        return () => window.removeEventListener("hashchange", handleHashChange);
    }, [children, changeUrl]);
    // Ensure children is treated as an array
    const childrenArray = Array.isArray(children) ? children : [children];
    // Filter out any unwanted elements (e.g., SvgIcon) if needed
    const filteredChildren = childrenArray.filter((child) => {
        if (React.isValidElement(child)) {
            return child.type !== SvgIcon;
        }
        return true;
    });
    return (_jsxs(Tabs, { variant: "default", style: {
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column"
        }, value: activeTab, onChange: handleTabChange, children: [_jsx(Tabs.List, { children: filteredChildren &&
                    React.Children.map(filteredChildren, (child, index) => {
                        if (React.isValidElement(child)) {
                            const childElement = child;
                            let tabName = "";
                            if (childElement.props.label) {
                                tabName = childElement.props.label;
                            }
                            return (_jsx(Tabs.Tab, { value: index.toString(), children: _jsxs(Flex, { align: "center", gap: "sm", children: [childElement.props.leftNode && (_jsx(Flex, { children: childElement.props.leftNode })), tabName, childElement.props.rightNode && (_jsx(Flex, { children: childElement.props.rightNode }))] }) }, index));
                        }
                    }) }), filteredChildren.map((child, index) => {
                if (React.isValidElement(child)) {
                    const isActiveTab = activeTab === index.toString();
                    return (_jsx(Tabs.Panel, { value: index.toString(), style: {
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                            marginTop: "0.5rem"
                        }, children: React.cloneElement(child, {
                            isActiveTab
                        }) }, index));
                }
                return (_jsx(Tabs.Panel, { value: index.toString(), children: child }, index));
            })] }));
};
